import React, { useState, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';

/* ═══════════════════════════════════════
   BoardList — 글 목록 (v2)
   ═══════════════════════════════════════ */

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ── Container ──
const ListWrapper = styled.div`
  animation: ${fadeIn} 0.4s ease both;
`;

// ── Search Bar ──
const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 10px 16px 10px 40px;
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  border-radius: ${({ theme }) => theme.borderRadius.base};
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.neutral[800]};
  background: ${({ theme }) => theme.colors.neutral[0]} url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%239ca3af' viewBox='0 0 24 24'%3E%3Cpath d='M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z'/%3E%3C/svg%3E") no-repeat 12px center;
  transition: border-color ${({ theme }) => theme.transition.fast},
              box-shadow ${({ theme }) => theme.transition.fast};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary[400]};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary[50]};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.neutral[400]};
  }

  @media (max-width: 640px) {
    width: 100%;
  }
`;

const WriteButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  background: ${({ theme }) => theme.colors.primary[500]};
  color: #fff;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.base};
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all ${({ theme }) => theme.transition.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.primary[700]};
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.elevation.md};
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 640px) {
    width: 100%;
    justify-content: center;
  }
`;

// ── Table ──
const TableContainer = styled.div`
  background: ${({ theme }) => theme.colors.neutral[0]};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1px solid ${({ theme }) => theme.colors.neutral[100]};
  box-shadow: ${({ theme }) => theme.elevation.sm};
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
`;

const Thead = styled.thead`
  background: ${({ theme }) => theme.colors.neutral[50]};

  @media (max-width: 640px) {
    display: none;
  }
`;

const Th = styled.th`
  padding: 14px 16px;
  font-size: 0.8rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutral[500]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: ${({ $align }) => $align || 'left'};
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  white-space: nowrap;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  transition: background ${({ theme }) => theme.transition.fast};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.primary[50]};
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.neutral[100]};
  }

  @media (max-width: 640px) {
    display: flex;
    flex-direction: column;
    padding: 14px 16px;
    gap: 6px;
  }
`;

const Td = styled.td`
  padding: 14px 16px;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.neutral[700]};
  text-align: ${({ $align }) => $align || 'left'};
  vertical-align: middle;

  @media (max-width: 640px) {
    padding: 0;
    text-align: left;
    font-size: 0.85rem;
  }
`;

const PostNum = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 24px;
  background: ${({ theme }) => theme.colors.neutral[100]};
  color: ${({ theme }) => theme.colors.neutral[500]};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 0.78rem;
  font-weight: 600;

  @media (max-width: 640px) {
    display: none;
  }
`;

const PostTitle = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.neutral[800]};
  transition: color ${({ theme }) => theme.transition.fast};
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${Tr}:hover & {
    color: ${({ theme }) => theme.colors.primary[600]};
  }

  @media (max-width: 640px) {
    font-weight: 600;
    font-size: 0.95rem;
    white-space: normal;
  }
`;

const PostMeta = styled.div`
  display: none;
  font-size: 0.78rem;
  color: ${({ theme }) => theme.colors.neutral[400]};

  @media (max-width: 640px) {
    display: flex;
    align-items: center;
    gap: 12px;
  }
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 3px;
`;

const AuthorName = styled.span`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.neutral[600]};
  font-weight: 500;

  @media (max-width: 640px) {
    display: none;
  }
`;

const ViewCount = styled.span`
  font-size: 0.82rem;
  color: ${({ theme }) => theme.colors.neutral[400]};

  @media (max-width: 640px) {
    display: none;
  }
`;

const DeleteBtn = styled.button`
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${({ theme }) => theme.colors.neutral[100]};
  color: ${({ theme }) => theme.colors.semantic.error};
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transition.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.semantic.error};
    color: #fff;
    border-color: ${({ theme }) => theme.colors.semantic.error};
  }
`;

const EmptyState = styled.div`
  padding: 60px 24px;
  text-align: center;
  color: ${({ theme }) => theme.colors.neutral[400]};

  svg {
    margin-bottom: 12px;
    opacity: 0.4;
  }

  p {
    font-size: 0.95rem;
    margin: 0;
  }
`;

// ── Pagination ──
const PaginationWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin-top: 24px;

  @media (max-width: 640px) {
    gap: 2px;
    margin-top: 20px;
  }
`;

const PageBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  padding: 0 8px;
  font-size: 0.85rem;
  font-weight: ${({ $active }) => ($active ? '700' : '500')};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.neutral[0] : theme.colors.neutral[600]};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.primary[500] : theme.colors.neutral[0]};
  border: 1px solid
    ${({ $active, theme }) =>
      $active ? theme.colors.primary[500] : theme.colors.neutral[200]};
  border-radius: ${({ theme }) => theme.borderRadius.base};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transition.fast};

  &:hover:not(:disabled) {
    background: ${({ $active, theme }) =>
      $active ? theme.colors.primary[600] : theme.colors.neutral[50]};
    border-color: ${({ $active, theme }) =>
      $active ? theme.colors.primary[600] : theme.colors.neutral[300]};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  @media (max-width: 640px) {
    min-width: 32px;
    height: 32px;
    font-size: 0.8rem;
  }
`;

const NavBtn = styled(PageBtn)`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.neutral[500]};
  background: ${({ theme }) => theme.colors.neutral[0]};
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.primary[600]};
    border-color: ${({ theme }) => theme.colors.primary[300]};
  }
`;

const TotalInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  font-size: 0.82rem;
  color: ${({ theme }) => theme.colors.neutral[400]};

  @media (max-width: 640px) {
    font-size: 0.78rem;
  }
`;

// ── Component ──
function BoardList({
  posts = [],
  pageInfo = {},
  session,
  searchQuery = '',
  onSearchChange,
  onReadPost,
  onDeletePost,
  onPageChange,
  onWriteClick,
}) {
  const {
    currentPage = 1,
    totalPages = 0,
    pageNumbers = [],
    hasNext = false,
  } = pageInfo;

  // Filter posts by search query (client-side supplement)
  const filteredPosts = useMemo(() => {
    if (!searchQuery.trim()) return posts;
    const q = searchQuery.toLowerCase();
    return posts.filter(
      (p) =>
        (p.notice_title || '').toLowerCase().includes(q) ||
        (p.notice_name || '').toLowerCase().includes(q)
    );
  }, [posts, searchQuery]);

  const isAdmin = session?.UserId === 'dexterity';

  return (
    <ListWrapper>
      {/* Search + Write */}
      <SearchBar>
        <SearchInput
          type="text"
          placeholder="제목 또는 작성자로 검색..."
          value={searchQuery}
          onChange={(e) => onSearchChange?.(e.target.value)}
        />
        {session?.UserId && (
          <WriteButton onClick={onWriteClick}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 5v14M5 12h14" />
            </svg>
            글쓰기
          </WriteButton>
        )}
      </SearchBar>

      <TotalInfo>
        <span>
          총 <strong>{filteredPosts.length}</strong>개의 글
        </span>
        {totalPages > 0 && (
          <span>
            {currentPage} / {totalPages} 페이지
          </span>
        )}
      </TotalInfo>

      {/* Table */}
      <TableContainer>
        <Table>
          <Thead>
            <tr>
              <Th $align="center" style={{ width: '64px' }}>No</Th>
              <Th>제목</Th>
              <Th $align="center" style={{ width: '120px' }}>작성자</Th>
              <Th $align="center" style={{ width: '80px' }}>조회</Th>
              {isAdmin && <Th $align="center" style={{ width: '72px' }}>관리</Th>}
            </tr>
          </Thead>
          <Tbody>
            {filteredPosts.length > 0 ? (
              filteredPosts.map((post, idx) => (
                <Tr key={post.num || idx} onClick={() => onReadPost?.(post)}>
                  <Td $align="center">
                    <PostNum>{post.num}</PostNum>
                  </Td>
                  <Td>
                    <PostTitle>{post.notice_title || '(제목 없음)'}</PostTitle>
                    <PostMeta>
                      <MetaItem>👤 {post.notice_name}</MetaItem>
                      <MetaItem>👁 {post.search_count}</MetaItem>
                    </PostMeta>
                  </Td>
                  <Td $align="center">
                    <AuthorName>{post.notice_name}</AuthorName>
                  </Td>
                  <Td $align="center">
                    <ViewCount>{post.search_count}</ViewCount>
                  </Td>
                  {isAdmin && (
                    <Td $align="center" onClick={(e) => e.stopPropagation()}>
                      <DeleteBtn onClick={() => onDeletePost?.(post)}>
                        삭제
                      </DeleteBtn>
                    </Td>
                  )}
                </Tr>
              ))
            ) : (
              <tr>
                <td colSpan={isAdmin ? 5 : 4}>
                  <EmptyState>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M9 12h6M12 9v6" strokeLinecap="round" />
                      <rect x="3" y="3" width="18" height="18" rx="4" />
                    </svg>
                    <p>{searchQuery ? '검색 결과가 없습니다.' : '게시물이 없습니다.'}</p>
                  </EmptyState>
                </td>
              </tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {pageNumbers.length > 0 && (
        <PaginationWrapper>
          <NavBtn
            disabled={currentPage <= 1}
            onClick={() => {
              if (currentPage > 1) onPageChange?.(currentPage - 1);
            }}
          >
            ‹ 이전
          </NavBtn>

          {pageNumbers.map((pageNum) => (
            <PageBtn
              key={pageNum}
              $active={currentPage === pageNum}
              onClick={() => onPageChange?.(pageNum)}
            >
              {pageNum}
            </PageBtn>
          ))}

          <NavBtn
            disabled={!hasNext}
            onClick={() => {
              if (hasNext) onPageChange?.(currentPage + 1);
            }}
          >
            다음 ›
          </NavBtn>
        </PaginationWrapper>
      )}
    </ListWrapper>
  );
}

export default BoardList;
